// =============================================================================
// Shared server-side list primitives: sort/filter types and a data-fetching
// hook used by every list page. Sorting and filtering run against the FULL
// dataset in Postgres (not just the current page), with debouncing and stale-
// response guarding for speed.
// =============================================================================
import { useCallback, useEffect, useRef, useState } from 'react'

export type SortDir = 'asc' | 'desc'
export interface Sort { column: string; dir: SortDir }

export type FilterOp = 'eq' | 'ilike' | 'gte' | 'lte'
export interface Filter { column: string; op: FilterOp; value: string | number | boolean }

export interface ListOptions {
  page?: number
  pageSize?: number
  search?: string
  sort?: Sort | null
  filters?: Filter[]
}

export type ListFetcher<T> = (opts: ListOptions) => Promise<{ rows: T[]; count: number }>

export interface UseServerList<T> {
  rows: T[]
  count: number
  page: number
  pageSize: number
  search: string
  sort: Sort | null
  filters: Filter[]
  loading: boolean
  setPage: (p: number) => void
  setSearch: (s: string) => void
  setFilters: (f: Filter[]) => void
  toggleSort: (column: string) => void
  reload: () => void
}

export function useServerList<T>(
  fetcher: ListFetcher<T>,
  options: { pageSize?: number; defaultSort?: Sort } = {},
): UseServerList<T> {
  const pageSize = options.pageSize ?? 25
  const [rows, setRows] = useState<T[]>([])
  const [count, setCount] = useState(0)
  const [page, setPageState] = useState(0)
  const [search, setSearchState] = useState('')
  const [sort, setSort] = useState<Sort | null>(options.defaultSort ?? null)
  const [filters, setFiltersState] = useState<Filter[]>([])
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)

  // Guard against out-of-order responses so fast typing never shows stale rows.
  const reqId = useRef(0)
  const filterKey = JSON.stringify(filters)
  const sortKey = sort ? `${sort.column}:${sort.dir}` : ''

  useEffect(() => {
    const id = ++reqId.current
    const debounce = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await fetcher({ page, pageSize, search, sort, filters })
        if (id === reqId.current) {
          setRows(res.rows)
          setCount(res.count)
        }
      } catch (err) {
        if (id === reqId.current) { setRows([]); setCount(0) }
        console.error('list fetch failed', err)
      } finally {
        if (id === reqId.current) setLoading(false)
      }
    }, 250)
    return () => clearTimeout(debounce)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher, page, pageSize, search, sortKey, filterKey, nonce])

  // Any change to the query (search/filter/sort) returns to the first page.
  const setSearch = useCallback((s: string) => { setSearchState(s); setPageState(0) }, [])
  const setFilters = useCallback((f: Filter[]) => { setFiltersState(f); setPageState(0) }, [])
  const setPage = useCallback((p: number) => setPageState(p), [])
  const toggleSort = useCallback((column: string) => {
    setPageState(0)
    setSort(prev => prev?.column === column ? { column, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { column, dir: 'asc' })
  }, [])
  const reload = useCallback(() => setNonce(n => n + 1), [])

  return { rows, count, page, pageSize, search, sort, filters, loading, setPage, setSearch, setFilters, toggleSort, reload }
}
